// Main.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HelpIcon, UserHeaderIcon, HeaderIconHover, LeftArrowIcon } from '../components/icons/Icons';
import { selectActiveMenuItem, setActiveMenuItem } from '../slicers/MainSlice';
import { resetUserState } from '../slicers/UserSlice';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import MainContent from './MainContent';
import Sidebar from './sidenav/Sidebar';
import { Dropdown, DropdownButton, Spinner } from 'react-bootstrap';
import PerinHealthAlert from '../components/PerinHealthAlert';
import Welcome from './app-tour/Welcome';
import ApplicationGuide from './app-tour/ApplicationGuide';
import { resetAppTourState, setFieldValue } from './app-tour/AppTourSlice';
import VideoCallContainer from '../components/VideoCallContainer';
import BellNotification from '../components/BellNotification';
import { closeCall, setCallInformation, openCallContainer, setJoiningStaffCall } from '../slicers/CallContainerSlice';
import CallNotification from '../components/Notifications/CallNotification';
import { closeCallNotification } from '../components/Notifications/CallNotificationSlice';
import { useWebSocket } from '../services/webSocket/useWebSocket';
import useAxios from '../services/AxiosConfig';
import AppointmentVideoCall from '../views/appointment/AppointmentVideoCall';
import { closeAppointment } from './appointment/AppointmentSlicer';
import UserOptionsCard from '../views/account/UserOptionsCard';
import ManageAccountModal from '../views/account/ManageAccountModal';
import useIdleTimeout from '../services/hooks/useIdleTimeout';
import IdleTimeoutAlert from '../components/IdleTimeoutAlert';
import PreferencesModal from './account/PreferencesModal';
import { toggleSidebar } from '../slicers/sidebarSlice';
import { openConfirmation, closeConfirmation } from '../components/ModalDirtyConfirmationSlice';
import { resetDirty } from '../slicers/DirtyCheckSlice';
import ModalDirtyConfirmation from '../components/ModalDirtyConfirmation';
import DeviceInformation from './device-management/DeviceInformation';
import { closeAlert } from '../components/PerinHealthAlertSlice';
import { showInactivityAlert } from '../slicers/TimeOutAlertSlice';
import Logger from '../services/Logger';
import { LogElements, LogAction } from '../services/LogElements';
import VersionModal from '../components/VersionModal';
import { showPasswordResetModal } from '../slicers/PasswordResetSlice';
import SystemBanner from '../components/SystemBanner';
import { detectSystemInfo } from '../utils/systemUtils';
import SecurityActivityModal from './account/SecurityActivityModal';
import ManufacturerInformationModal from './account/ManufacturerInformationModal';
import {
  setOnboardingPendingPath,
  clearOnboardingPendingPath,
} from '../slicers/OnboardingNavigationGuardSlice';


const Main = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo);
  const { id, fullName, firstName, lastName, inactivityTimeout } = userInfo;
  const { sendMessage } = useWebSocket();
  const location = useLocation();
  const isDirty = useSelector((state) => state.dirty.isDirty);
  const modalInfo = useSelector((state) => state.modalDirty);

  const axiosConfig = useSelector((state) => state.axiosConfig);
  const axios = useAxios(axiosConfig.envURL, dispatch);
  const perinHealthAlert = useSelector((state) => state.perinHealthAlert);
  const appTour = useSelector((state) => state.appTour);
  const callNotification = useSelector((state) => state.callNotification);
  const onboardingInProgress = useSelector((state) => state.onboardingNavigationGuard.inProgress);
  const onboardingPendingPath = useSelector((state) => state.onboardingNavigationGuard.pendingPath);

  const sidebarSlicer = useSelector((state) => state.sidebar);
  const isSidebarOpen = sidebarSlicer.isSidebarOpen;
  const activeMenuItem = useSelector(selectActiveMenuItem);

  const { callContainerShowing, audioOnly, callData, expanded } = useSelector((state) => state.callContainer);
  const appointmentInfo = useSelector((state) => state.appointment);

  const [showUserOptions, setShowUserOptions] = useState(false);
  const [showManageAccount, setShowManageAccount] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showModalNotifications, setShowModalNotifications] = useState(false);
  const [showVersionSettings, setShowVersionSettings] = useState(false);
  const [showSecurityActivity, setShowSecurityActivity] = useState(false);
  const [showManufacturerInfo, setShowManufacturerInfo] = useState(false);
  const iconRef = useRef(null);

  const handleMenuItemClick = (menuItem) => {
    dispatch(setActiveMenuItem(menuItem));
  };

  const handleSignOut = async () => {
    // Log logout button click
    Logger.shared.info(
      LogElements.logoutButton,
      LogAction.clicked,
      'Main.js'
    );

    try {
      await axios.post('/signout')
    } catch (error) {
      console.error('Failed to sign out:', error);
    }

    // Log user logout
    Logger.shared.debug(`User logged out`, null, 'Main.js');
    
    // Clear Logger's stored user ID when signing out
    Logger.shared.clearUserId();
    
    dispatch(resetUserState());
    navigate('/');
  };


  const handleTimeout = () => {
    dispatch(showInactivityAlert());
    handleSignOut();
  };

  const showIdleTimeout = useIdleTimeout(
    inactivityTimeout * 60 * 1000, 
    handleTimeout,
    (inactivityTimeout - 0.5) * 60 * 1000
  );

  const handleCallContainerClose = () => {
    dispatch(closeCall());
  };

  const handleCloseAppointment = () => {
    dispatch(closeAppointment());
  };

  const handleItemClick = (event) => {
    const clickedText = event.target.textContent;
    if (clickedText === 'Instructions for Use') {
      navigate('/instructions');
    } else if (clickedText === 'Onboarding') {
      if (!isSidebarOpen) {
        dispatch(toggleSidebar());
      }
      dispatch(resetAppTourState());
      dispatch(setFieldValue({ name: 'showWelcome', value: true }));
    }
  };


  const dismissCallNotification = async () => {
    dispatch(closeCallNotification());
    try {
      await axios.delete(`/meeting/${callNotification.meetingId}`);
    } catch (err) {
      console.error('Failed to delete meeting:', err);
    }
    sendMessage({
      type: 'staff_decline',
      senderId: id,
      senderName: fullName,
      recipientId: callNotification.id,
      recipientName: callNotification.name,
      meetingId: callNotification.meetingId
    });
  };

  const acceptCallNotification = () => {
    const callData = {
      title: `Staff Message ${callNotification.audioOnly ? "Audio" : "Video"} Call - ${fullName} & ${callNotification.name}`,
      appointmentId: null,
      senderId: id,
      recipientId: callNotification.id,
      senderName: fullName,
      recipientName: callNotification.name,
      meetingId: callNotification.meetingId
    };
    dispatch(setJoiningStaffCall());
    dispatch(closeCallNotification());
    dispatch(setCallInformation({ "audioOnly": callNotification.audioOnly, "callData": callData, viewType: "staffMessage" }));
    dispatch(openCallContainer());
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iconRef.current && !iconRef.current.contains(event.target)) {
        setShowUserOptions(false);
      }
      setShowModalNotifications(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [iconRef, showModalNotifications]);

  useEffect(() => {
    dispatch(resetDirty());
  }, [location.pathname, dispatch]);

  const systemInfo = detectSystemInfo();

  const handleManageAccountClick = () => {
    // Log Manage Account button click
    Logger.shared.info(
      LogElements.manageAccountButton,
      LogAction.clicked,
      'Main.js'
    );
    setShowManageAccount(true);
    setShowUserOptions(false);
  };

  const handleManageAccountClose = () => {
    setShowManageAccount(false);
  };

  const handlePreferencesClick = () => {
    // Log Preferences button click
    Logger.shared.info(
      LogElements.preferencesButton,
      LogAction.clicked,
      'Main.js'
    );
    setShowPreferences(true);
    setShowUserOptions(false);
  };

  const handlePreferencesClose = () => {
    setShowPreferences(false);
    setShowUserOptions(false);
  };

  const handleVersionClick = () => {
    setShowVersionSettings(true);
    setShowUserOptions(false);
  };

  const handleChangePasswordClick = () => {
    // Log Change Password button click
    Logger.shared.info(
      LogElements.changePasswordButton,
      LogAction.clicked,
      'Main.js'
    );
    setShowUserOptions(false);
    // Show password change modal
    dispatch(showPasswordResetModal({
      type: 'change',
      message: '',
      daysSinceLastChange: 0,
      policyDays: 0,
      daysUntilExpiry: 0,
      urgency: 'warning',
      lastPasswordChange: null
    }));
  };

  const handleSecurityActivityClick = () => {
    // Log Security Activity button click
    Logger.shared.info(
      LogElements.securityActivityButton,
      LogAction.clicked,
      'Main.js'
    );
    setShowUserOptions(false);
    setShowSecurityActivity(true);
  };

  const handleSecurityActivityClose = () => {
    setShowSecurityActivity(false);
    setShowUserOptions(false);
  };

  const handleManufacturerInfoClick = () => {
    Logger.shared.info(
      LogElements.manufacturerInformationButton,
      LogAction.clicked,
      'Main.js'
    );
    setShowUserOptions(false);
    setShowManufacturerInfo(true);
  };

  const handleManufacturerInfoClose = () => {
    setShowManufacturerInfo(false);
  };

  const handlePreferencesSaved = () => {
    // Log preferences save
    Logger.shared.info(
      LogElements.saveButton,
      LogAction.clicked,
      'Main.js',
      'User preferences updated'
    );
  };



  const titleHidden = [
    'patient-detail',
    'new-organization-management',
    'add-new-organization',
    'create-account',
    'add-edit-role',
    'add-reference-code',
    'update-alerts-priority',
    'add-device',
    'add-tablet',
    'tablet-registry',
    'add-edit-content',
    'questionnaire-setup',
    'add-questionnaire',
    'preview-question',
    'add-program',
    'enroll-patient',
    'device-group-assignment',
    'hub-management-patient',
    'device-group-assignment',
    'device-information',
    'view-program',
    'patient-security',
    'update-patch',
    'inpatient-portal'
  ].some(path => location.pathname.includes(path));

  const navigatePaths = {
    'new-organization-management': { path: '/organization-management', name: "Hierarchy" },
    'add-new-organization': { path: '/new-organization', name: "Organization" },
    'onboarding': { path: '/onboarding', name: "Onboarding" },
    'create-account': { path: '/account-management', name: "Accounts" },
    'add-edit-role': { path: '/role-management', name: "Roles" },
    'add-reference-code': { path: '/reference-code', name: "Reference Code" },
    'add-device': { path: '/device-management', name: "Devices" },
    'add-tablet': { path: '/tablet-management', name: "Tablets" },
    'tablet-registry': { path: '/tablet-management', name: "Tablets" },
    'add-edit-content': { path: '/content-management', name: "Content" },
    'questionnaire-setup': { path: '/questionnaire-management', name: "Questionnaire" },
    'add-questionnaire': { path: '/questionnaire-management', name: "Questionnaire" },
    'preview-question': { path: '/questionnaire-management', name: "Questionnaire" },
    'add-program': { path: '/program-management', name: "Care Program" },
    'enroll-patient': { path: '/patient-enrollment', name: "Enrollment" },
    'device-group-assignment': { path: '/device-management', name: "Devices" },
    'hub-management-patient': { path: '/hub-management', name: "Hub" },
    'device-group-assignment': { path: '/device-management', name: "Devices" },
    'view-program': { path: '/patient-enrollment', name: "Enrollment" } ,
    'patient-security': { path: '/security-management', name: "Security" } ,
    'update-alerts-priority': { path: '/reference-code', name: "Reference Code" },
    'update-patch': { path: '/inpatient-portal', name: "Inpatient Portal" }
  };
  const baseKey = location.pathname.split('/')[1]; 
  let matchingPath = navigatePaths[baseKey];

  if(location.search != ''){
    if(location.search.includes("ehr=true")){
      matchingPath = { path: '/ehr-accounts', name: "EHR Accounts" };
    }
  }


  const onBackHandler = () => {
    if (onboardingInProgress) {
        dispatch(setOnboardingPendingPath(matchingPath?.path || null));
        dispatch(
          openConfirmation({
                header: 'Confirmation',
                message: 'Onboarding is still in progress. Leave anyway?',
                buttons: [
                    { label: 'No', variant: 'secondary', onClickKey: 'NO' },
                    { label: 'Yes', variant: 'primary', onClickKey: 'YES_ONBOARDING' },
                ],
            })
        );
    } else if (isDirty) {
        dispatch(
          openConfirmation({
                header: 'Confirmation',
                message: "You have unsaved changes. Are you sure you want to leave?",
                buttons: [
                    { label: 'No', variant: 'secondary', onClickKey: 'NO' },
                    { label: 'Yes', variant: 'primary', onClickKey: 'YES' },
                ],
            })
        );
    }else {
      navigate(matchingPath.path);
    }
    dispatch(closeAlert());
};

const handleDialogButtonClick = (onClickKey) => {
    if (onClickKey === 'YES') {
        navigate(matchingPath.path);  
        dispatch(resetDirty());
    } else if (onClickKey === 'YES_ONBOARDING') {
        navigate(onboardingPendingPath || matchingPath?.path || '/onboarding');
        dispatch(resetDirty());
    }
    dispatch(clearOnboardingPendingPath());
    dispatch(closeConfirmation());
};

  return (
    <div className={`main-container ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {axiosConfig.requestsCount !== 0 && (
        <div className="overlay">
          <Spinner className="spinner" animation="border" role="status" variant="secondary" style={{ width: '6rem', height: '6rem' }} />
        </div>
      )}
      <div className={`sidebar ${isSidebarOpen ? 'sidebar-opened' : 'sidebar-minimized'}`}>
        <Sidebar onMenuItemClick={handleMenuItemClick} />
      </div>
      <div className={`main-content ${isSidebarOpen ? 'content-with-sidebar' : 'full-width-content'}`}>
        <div className="sub-content" style={{ display: 'flex', flexDirection:"column"}}>
          <div style={{ display: "flex", flexDirection: "column", width:"100%"}}>
            <div className="userInfo-content">
              <div className="header-icon-container">
                <div className="header-icon-hover-bg">
                  <HeaderIconHover />
                </div>
                <DropdownButton variant="secondary" title={<HelpIcon />} className="help-icon">
                  <Dropdown.Item onClick={handleItemClick}>Instructions for Use</Dropdown.Item>
                  <Dropdown.Item onClick={handleItemClick}>FAQ</Dropdown.Item>
                  <Dropdown.Item onClick={handleItemClick}>Onboarding</Dropdown.Item>
                </DropdownButton>
              </div>
              <div className="header-icon-container">
                <div className="header-icon-hover-bg">
                <HeaderIconHover />
                </div>
                <BellNotification/>
              </div>
              <div ref={iconRef} className="header-icon-container">
                <div onClick={() => setShowUserOptions((prev) => !prev)}>
                  <div className="header-icon-hover-bg">
                  <HeaderIconHover />
                  </div>
                  {userInfo.profilePictureURL !== "" ?
                    <div className="user-profile-header-container">
                      <img
                        src={userInfo.profilePictureURL}
                        alt="Profile_Picture"
                        style={{
                          width: '24px',          
                          height: '24px',
                          borderRadius: '50%',
                          border: '1.25px solid #373737',
                          objectFit: 'cover',
                          cursor: 'pointer',
                          position: 'relative'
                      }}
                      />
                    </div>
                    :
                    <div className="user-profile-header-container">
                      <span className={"profile-initials"} style={{cursor:"pointer", fontSize:"12px", fontWeight:"700"}}>
                        {`${firstName?.[0]}${lastName?.[0]}`}
                      </span>
                      <UserHeaderIcon/>
                    </div>
                  }
                </div>
                {showUserOptions && (
                  <UserOptionsCard
                    onManageAccountClick={handleManageAccountClick}
                    onPreferencesClick={handlePreferencesClick}
                    onSignOut={handleSignOut}
                    onVersionClick={handleVersionClick}
                    onChangePasswordClick={handleChangePasswordClick}
                    onSecurityActivityClick={handleSecurityActivityClick}
                    onManufacturerInfoClick={handleManufacturerInfoClick}
                  />
                )}
              </div>
              <div className={'user-fullName'}>{fullName}</div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <SystemBanner 
              show={systemInfo.show}
              type={systemInfo.type}
              data={systemInfo.data}
            />
          </div>
  
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!titleHidden ?
              <span className="selectedMenuItem">{sidebarSlicer.selectedMenuItem }</span>
              : matchingPath ?
              <div className="selectedMenuItemBack" onClick={onBackHandler}>
                <LeftArrowIcon />
                <span>{`Back to ${matchingPath ? matchingPath.name : ""}`}</span>
              </div>
              :
              <span></span>
            }
          </div>
        </div>

        {callNotification.show && (
          <CallNotification
            show={callNotification.show}
            fullName={callNotification.name}
            onDismiss={dismissCallNotification}
            onAccept={acceptCallNotification}
          />
        )}
        {perinHealthAlert.show && (
          <PerinHealthAlert
            show={perinHealthAlert.show}
            type={perinHealthAlert.type}
            header={perinHealthAlert.header}
            message={perinHealthAlert.message}
            dismissable={perinHealthAlert.dismissable}
          />
        )}
        <Routes>
          <Route path="/*" element={<MainContent activeMenuItem={activeMenuItem} />} />
          <Route path="/device-information" element={<DeviceInformation />} />
        </Routes>
        <Welcome />
        <ApplicationGuide />
      </div>
      {callContainerShowing &&
        <div style={expanded ? { display: "flex", position: "absolute", zIndex: "1000", alignitems: "center", justifyContent: "center", width: "100%", height: "100%", alignItems: "center" } : { bottom: "20px", right: "30px", position: "absolute", zIndex: "1000" }}>
          <VideoCallContainer
            onClose={handleCallContainerClose}
            audioOnly={audioOnly}
            data={callData}
          />
        </div>
      }
      {
          modalInfo.show && (
              <ModalDirtyConfirmation
                  show={modalInfo.show}
                  header={modalInfo.header}
                  message={modalInfo.message}
                  buttons={modalInfo.buttons}
                  handleButtonClick={handleDialogButtonClick}
              />
          )
      }
      {appointmentInfo.showing &&
        <div style={!appointmentInfo.minimized ? { display: "flex", position: "absolute", zIndex: "1000", alignitems: "center", justifyContent: "center", width: "100%", height: "100%", alignItems: "center" } : { bottom: "20px", right: "30px", position: "absolute", zIndex: "1000" }}>
          <AppointmentVideoCall
            onClose={handleCloseAppointment}
            data={appointmentInfo.appointmentData}
          />
        </div>
      }
      <ManageAccountModal show={showManageAccount} handleClose={handleManageAccountClose} />
      <PreferencesModal show={showPreferences} handleClose={handlePreferencesClose} onPreferencesSaved={handlePreferencesSaved} />
      <VersionModal show={showVersionSettings} handleClose={()=>setShowVersionSettings(false)} />
      <SecurityActivityModal show={showSecurityActivity} handleClose={handleSecurityActivityClose} />
      <ManufacturerInformationModal show={showManufacturerInfo} handleClose={handleManufacturerInfoClose} />
      <IdleTimeoutAlert
        show={showIdleTimeout}
      />
    </div>
  );
};

export default Main;
